import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useFirstVisible } from './useFirstVisible';

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];

/**
 * Engine shared by the composables below. `morph(target)` takes the displayed
 * text to `target`: the characters that differ turn into random glyphs one by
 * one, extra glyphs are dropped (or missing ones pop in) one by one, then they
 * lock into the real characters one by one. Spaces are never scrambled, so the
 * words keep their shape. Scrambled glyphs re-roll every `flickerDelay`.
 * Delays are in ms.
 */
function createScramble({ appearDelay = 50, resolveDelay = 60, scrambleDelay = 40, eraseDelay = 30, flickerDelay = 50 } = {}) {
    const text = ref('');
    // One entry per displayed character: the real one, or null while scrambled.
    const cells = [];
    let script = null;
    let stepTimer = null;
    let flickerTimer = null;

    const render = () => {
        text.value = cells.map((char) => char ?? randomGlyph()).join('');
    };

    /** Yields the delay after each step. The prefix shared with `target` is left as is. */
    function* morph(target) {
        let start = 0;
        while (start < cells.length && cells[start] === target[start]) start++;
        for (let i = start; i < cells.length; i++) {
            if (cells[i] === null || cells[i] === ' ') continue;
            cells[i] = null;
            yield scrambleDelay;
        }
        while (cells.length > target.length) {
            cells.pop();
            yield eraseDelay;
        }
        while (cells.length < target.length) {
            cells.push(target[cells.length] === ' ' ? ' ' : null);
            yield appearDelay;
        }
        for (let i = start; i < target.length; i++) {
            if (cells[i] === target[i]) continue;
            cells[i] = target[i];
            yield resolveDelay;
        }
    }

    const stop = () => {
        clearTimeout(stepTimer);
        clearInterval(flickerTimer);
    };

    const step = () => {
        const { value: delay, done } = script.next();
        render();
        if (done) stop();
        else stepTimer = setTimeout(step, delay);
    };

    /** Runs `steps` (a generator of delays), replacing whatever was running. */
    const play = (steps) => {
        stop();
        script = steps;
        flickerTimer = setInterval(render, flickerDelay);
        step();
    };

    return { text, morph, play, stop };
}

/** Scrambles from one text to the next, holding each for `holdDelay`, looping forever while mounted. */
export function useScrambleLoop(texts, { holdDelay = 2000, ...options } = {}) {
    const { text, morph, play, stop } = createScramble(options);

    function* loop() {
        for (let index = 0; ; index = (index + 1) % texts.length) {
            yield* morph(texts[index]);
            yield holdDelay;
        }
    }

    onMounted(() => play(loop()));
    onBeforeUnmount(stop);

    return text;
}

/**
 * Scrambles `source()` in `startDelay` after the `el` template ref first scrolls
 * into view, then scrambles to each new value it takes. Snappier than the loop
 * by default, as it is meant for headings.
 */
export function useScrambleReveal(
    el,
    source,
    { startDelay = 0, appearDelay = 30, resolveDelay = 40, scrambleDelay = 25, eraseDelay = 20, ...options } = {}
) {
    const { text, morph, play, stop } = createScramble({ appearDelay, resolveDelay, scrambleDelay, eraseDelay, ...options });
    let revealed = false;

    function* reveal() {
        yield startDelay;
        yield* morph(source());
    }

    watch(source, (value) => {
        if (revealed) play(morph(value));
    });

    useFirstVisible(el, () => {
        revealed = true;
        play(reveal());
    });
    onBeforeUnmount(stop);

    return text;
}
